import { HumanName, Patient, Practitioner, RelatedPerson } from "@medplum/fhirtypes";

/**
 * Picks the `HumanName` used for legal-style display and initials: `use === "official"` if present,
 * otherwise the first entry in `names`. Pass `client?.name` for a Patient, Practitioner, or RelatedPerson.
 */
export const getCanonicalHumanName = (names: HumanName[] | undefined): HumanName | undefined => {
  if (!names?.length) {
    return undefined;
  }
  return names.find((n) => n.use === "official") || names[0];
};

export const getAllNames = (client: Patient | Practitioner | RelatedPerson | undefined) => {
  if (!client) {
    return { firstName: "Name Unavailable", lastName: "Unknown", preferredName: "Unknown" };
  }
  const officialName = client.name?.find((name) => name.use === "official");

  const firstName = officialName?.given?.[0] || "Name Unavailable";
  const lastName = officialName?.family || "Unknown";
  const usualName = client.name?.find((name) => name.use === "usual");
  const preferredName =
    usualName?.given?.[0] ||
    officialName?.given?.[0] ||
    client.name?.[0]?.given?.[0] + " " + client.name?.[0]?.family ||
    "Name Unavailable";
  return { firstName, lastName, preferredName };
};

export const getGivenName = (client: Patient | Practitioner | RelatedPerson | undefined) => {
  if (!client) {
    return "Name Unavailable";
  }
  const givenName = client.name?.[0]?.given?.[0];
  const familyName = client.name?.[0]?.family;
  if (!givenName || !familyName) {
    return "Name Unavailable";
  }
  return `${givenName} ${familyName}`;
};

export const getDisplayName = (client: Patient | Practitioner | RelatedPerson | undefined) => {
  const { firstName, preferredName } = getAllNames(client);
  return preferredName || firstName;
};

export const getNameAriaLabel = (client: Patient | Practitioner | RelatedPerson | undefined) => {
  const { preferredName } = getAllNames(client);
  return preferredName ? "Client Preferred Name" : "Client First Name";
};

export const getProfilePicture = (client: Patient | Practitioner | RelatedPerson | undefined) => {
  if (!client) {
    return null;
  }
  const profilePicture = client.photo?.[0]?.url;
  return profilePicture || null;
};

/**
 * Extracts qualifications from a practitioner resource
 */
export const getPractitionerQualifications = (practitioner: Practitioner | undefined): string => {
  if (!practitioner) return "";

  return (
    practitioner.qualification
      ?.flatMap((q) => q.code.coding?.map((c) => c.display).filter(Boolean) ?? [])
      .join(", ") ?? ""
  );
};

/**
 * Gets the full practitioner name with qualifications
 */
export const getPractitionerNameWithCredentials = (
  practitioner: Practitioner | undefined,
  fallbackName?: string,
): string => {
  const qualifications = getPractitionerQualifications(practitioner);

  // Use practitioner name from the resource, fallback to provided name
  const practitionerGivenName = practitioner?.name?.[0]?.given?.[0];
  const practitionerFamilyName = practitioner?.name?.[0]?.family;
  const practitionerFullName =
    practitionerGivenName && practitionerFamilyName
      ? `${practitionerGivenName} ${practitionerFamilyName}`
      : fallbackName || "";

  // Only add dash and qualifications if qualifications exist
  return qualifications
    ? `${practitionerFullName} - ${qualifications}`.trim()
    : practitionerFullName.trim();
};

/**
 * Gets initials from first given name and family name (not middle name)
 * Falls back to any available name entry if official name is not found
 * Returns undefined if no valid initials can be computed, allowing Mantine Avatar to use its default behavior
 */
export const getInitialsFromName = (
  client: Patient | Practitioner | RelatedPerson | undefined,
): string | undefined => {
  const name = getCanonicalHumanName(client?.name);
  if (!name) {
    return undefined;
  }

  const firstInitial = name?.given?.[0]?.[0]?.toUpperCase();
  const lastInitial = name?.family?.[0]?.toUpperCase();

  // Only return initials if we have both first and last initial
  // If not, return undefined to let Mantine Avatar use its default behavior
  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`;
  }

  return undefined;
};
